import { Box, Typography, useTheme } from "@mui/material";
import { LeadListingsLabel } from "@/modules/lead-page/types";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { ListingProperty } from "@/modules/listing-dashboard/dtos/index-v2";
import { getListingDisplayValue } from "@/modules/lead-page/new/utils/listing-format";
import { leadListingsLabels } from "@/modules/lead-page/new/constants";
import ListingsActions from "@/modules/lead-page/components/lead-listings/listings-actions";
import { useGetActiveListing } from "@/modules/property-tracks/hooks/listings.hook";
import { ListingType } from "@/modules/property-tracks/dtos";

interface PropertiesListingProps {
  data: ListingProperty;
}

export const PropertiesListingCard = ({
  data: { property },
}: PropertiesListingProps) => {
  const theme = useTheme();
  const screen = useScreenSize();

  const {data} = useGetActiveListing({params: {propertyUid: property.uid, listingType: ListingType.RESALE}});

  return (
    <Box
      sx={{
        backgroundColor: "#F9F9F9",
        borderRadius: theme.nawy.radius.xl,
        padding: theme.nawy.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        width: "100%",
      }}
    >
      {/* Section 1: Title and Subtitle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.xxxs,
        }}
      >
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.lg.semibold,
            color: theme.palette.text.primary,
          }}
        >
          {`${property?.propertyType?.name || "N/A"} in ${
            property?.compound?.name || "N/A"
          }`}
        </Typography>
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.regular,
            color: theme.palette.text.secondary,
          }}
        >
          {`${property?.unitAddress || "N/A"}`}
        </Typography>
      </Box>

      {/* Section 2: Grid of Data Fields */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: theme.nawy.spacing.md,
          rowGap: theme.nawy.spacing.md,
          columnGap: theme.nawy.spacing["2xl"],
          width: "100%",
        }}
      >
        {leadListingsLabels.map((title: LeadListingsLabel) => {
          const value = property[title.key];
          const displayValue = getListingDisplayValue(title.key, value);

          return (
            <Box
              key={title.key}
              sx={{
                width: screen.isMobile ? "134px" : "159px",
                display: "flex",
                flexDirection: "column",
                gap: theme.nawy.spacing.xxs,
              }}
            >
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.palette.text.secondary,
                }}
              >
                {title.label}
              </Typography>
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.palette.text.primary,
                }}
              >
                {displayValue}
              </Typography>
            </Box>
          );
        })}

        <Box
              key={"websiteListingId"}
              sx={{
                width: screen.isMobile ? "134px" : "159px",
                display: "flex",
                flexDirection: "column",
                gap: theme.nawy.spacing.xxs,
              }}
            >
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                  color: theme.palette.text.secondary,
                }}
              >
                {"Website Listing ID"}
              </Typography>
              <Typography
                sx={{
                  ...theme.nawy.typography.english.variants.body.md.regular,
                  color: theme.palette.text.primary,
                }}
              >
                {data?.inventoryListingId ?? "N/A"}
              </Typography>
            </Box>
      </Box>

      {/* Section 3: Actions */}
      <Box>
        <ListingsActions
          unitPrice={property?.actualPrice}
          downpayment={property?.downpayment}
          remainingInstallments={property?.remainingInstallments}
          maintenance={property?.maintenance}
          listingId={property?.uid}
          compoundId={property?.compound?.id}
          websiteListingId={data?.inventoryListingId}
          trackUid={property?.propertyTrack?.[0]?.uid}
          ownersListingId={property?.ownersListingId ?? 0}
          trackTypeId={property?.propertyTrack?.[0]?.trackType?.id}
          numberOfBedrooms={property?.numberOfBedrooms ?? property?.bedrooms ?? undefined}
          numberOfBathrooms={property?.numberOfBathrooms ?? property?.bathrooms ?? undefined}
          unitArea={property?.unitArea}
          floorNumber={property?.floorNumber}
          finishing={property?.finishing}
          listingUid={data?.uid}
        />
      </Box>
    </Box>
  );
};

export default PropertiesListingCard;
